// "Your passkeys" in Settings (#660, #661): the console's first factor-management
// screen. It lists the signed-in user's passkeys, enrols a new one, and removes
// one.
//
// **An enrolment is four calls, in this order**: a step-up for
// `passkey_register` (#658 makes the server refuse an enrolment without one),
// the registration options, the browser's create prompt, and the answer sent
// back with the name and the grant. Each can refuse, and a refusal at any step
// adds nothing and says why. The step-up is the existing dialog, so it is
// proven by the password or a factor the user already holds, never by the
// passkey being enrolled.
//
// **A removal is two calls**: a step-up for `passkey_remove`, then the delete
// with the grant in its body. It signs the user out everywhere else (#787), and
// removing the last passkey of a user without TOTP ends their login's second
// factor (`auth/service.py::remove_passkey`), so the confirm says both before it
// is pressed. A passkey already gone — removed from
// another device between the list read and the confirm — answers 404, and the
// screen says so and reads the list again rather than keep a stale row.
//
// The passkeys belong to the user, not to the workspace being read, so the
// section does not depend on a context.

import { useCallback, useEffect, useId, useRef, useState } from "react";

import { ApiError, messageOf } from "../api/http";
import {
  PASSKEY_REGISTER_PURPOSE,
  PASSKEY_REMOVE_PURPOSE,
  stepUpFactorsOf,
  type MfaStatus,
  type PasskeyActions,
  type PasskeyRow,
} from "../api/passkeys";
import { Chip, Field, Notice } from "./chrome";
import { passkeyAttestation, type CredentialCreator } from "./passkey";
import { StepUpDialog, type StepUpProof } from "./StepUp";

const day = (stamp: string): string => stamp.slice(0, 10);

function PasskeyTable({
  rows,
  onRemove,
}: {
  rows: PasskeyRow[];
  onRemove: (row: PasskeyRow) => void;
}) {
  return (
    <div className="table-wrap" tabIndex={0}>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Added</th>
            <th scope="col">Last used</th>
            <th scope="col">Synced</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.credential_id}>
              <td>
                <strong>{row.name || "Unnamed passkey"}</strong>
              </td>
              <td>{day(row.created_at)}</td>
              <td>{row.last_used_at === null ? "never" : day(row.last_used_at)}</td>
              <td>{row.backed_up ? <Chip tone="good">synced</Chip> : "this device only"}</td>
              <td>
                <button
                  type="button"
                  className="btn btn--small btn--danger"
                  onClick={() => onRemove(row)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Enrol({
  actions,
  mfa,
  create,
  onDone,
  onCancel,
}: {
  actions: PasskeyActions;
  mfa: MfaStatus;
  create?: CredentialCreator;
  onDone: (name: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [proof, setProof] = useState<StepUpProof | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Each attempt spends its proof: a passkey's ceremony token is single-use,
  // and a retry that re-sent it would be refused and counted against the
  // lockout. So every attempt ends with a fresh dialog and no proof (#660, #661).
  const [attempt, setAttempt] = useState(0);
  const spent = () => {
    setProof(null);
    setAttempt((n) => n + 1);
  };

  // A user with no password and no factor has nothing to step up with; the
  // server lets a fresh session stand in for the first passkey (#658).
  const factors = stepUpFactorsOf(mfa);
  const nothingToProve = !factors.passkey && !factors.totp && !factors.password;

  const add = async () => {
    setProblem(null);
    setBusy(true);
    try {
      let grant: string | null = null;
      if (!nothingToProve) {
        if (proof === null) return;
        grant = await actions.stepUp(proof, PASSKEY_REGISTER_PURPOSE);
      }
      const began = await actions.beginRegistration();
      const answered = await passkeyAttestation(began, create);
      if (answered === null) {
        setProblem("The create prompt was dismissed. Nothing was added; try again when ready.");
        return;
      }
      await actions.finishRegistration({ ...answered, name: name.trim(), step_up_grant: grant });
      await onDone(name.trim());
    } catch (failure) {
      setProblem(`${messageOf(failure)} Nothing was added.`);
    } finally {
      setBusy(false);
      spent();
    }
  };

  const unproven = !nothingToProve && (proof === null || proof.credential === "");
  return (
    <div className="panel__body stack stack--tight" data-testid="add-passkey">
      <h3>Add a passkey</h3>
      <p className="muted">
        Adding a passkey signs you out everywhere else: every other session of yours ends, and
        this one stays signed in.
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <Field
        id="passkey-name"
        label="Name this passkey"
        placeholder="Work laptop"
        disabled={busy}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {nothingToProve ? (
        <p className="muted">
          You have no password or second factor to confirm this with, so your first passkey can
          be added only soon after signing in. If it is refused, sign in again and retry.
        </p>
      ) : (
      <StepUpDialog
        key={attempt}
        purpose={PASSKEY_REGISTER_PURPOSE}
        factors={stepUpFactorsOf(mfa)}
        disabled={busy}
        beginPasskey={actions.beginPasskey}
        onProofChange={setProof}
        onError={setProblem}
      />
      )}
      <div className="actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || unproven}
          onClick={() => void add()}
        >
          {busy ? "Waiting for your passkey…" : "Create passkey"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The outcome of a removal, for the section to show and to re-read after. */
type Removed = { kind: "removed"; name: string } | { kind: "gone"; name: string };

function Remove({
  actions,
  mfa,
  row,
  last,
  onDone,
  onCancel,
}: {
  actions: PasskeyActions;
  mfa: MfaStatus;
  row: PasskeyRow;
  /** Whether this is the user's only passkey, as the list last read it. */
  last: boolean;
  onDone: (outcome: Removed) => Promise<void>;
  onCancel: () => void;
}) {
  const [proof, setProof] = useState<StepUpProof | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Each attempt spends its proof: a passkey's ceremony token is single-use,
  // and a retry that re-sent it would be refused and counted against the
  // lockout. So every attempt ends with a fresh dialog and no proof (#660, #661).
  const [attempt, setAttempt] = useState(0);
  const spent = () => {
    setProof(null);
    setAttempt((n) => n + 1);
  };
  const name = row.name || "Unnamed passkey";
  const consequence = useId();

  const confirm = async () => {
    if (proof === null) return;
    setProblem(null);
    setBusy(true);
    try {
      const grant = await actions.stepUp(proof, PASSKEY_REMOVE_PURPOSE);
      try {
        await actions.remove(row.credential_id, grant);
      } catch (failure) {
        if (failure instanceof ApiError && failure.status === 404) {
          await onDone({ kind: "gone", name });
          return;
        }
        throw failure;
      }
      await onDone({ kind: "removed", name });
    } catch (failure) {
      setProblem(`${messageOf(failure)} Nothing was removed.`);
    } finally {
      setBusy(false);
      spent();
    }
  };

  const unproven = proof === null || proof.credential === "";
  return (
    <div className="panel__body stack stack--tight" data-testid="remove-passkey">
      <h3>Remove “{name}”</h3>
      <p className="muted" id={consequence}>
        Removing a passkey signs you out everywhere else: every other session of yours ends, and
        this one stays signed in.
      </p>
      {last && !mfa.totp_enabled ? (
        <Notice tone="bad">
          This is your only passkey and you have no authenticator app, so after removing it
          sign-in will no longer ask for a second factor.
        </Notice>
      ) : null}
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <StepUpDialog
        key={attempt}
        purpose={PASSKEY_REMOVE_PURPOSE}
        factors={stepUpFactorsOf(mfa)}
        disabled={busy}
        beginPasskey={actions.beginPasskey}
        onProofChange={setProof}
        onError={setProblem}
      />
      <div className="actions">
        <button
          type="button"
          className="btn btn--danger"
          disabled={busy || unproven}
          aria-describedby={consequence}
          onClick={() => void confirm()}
        >
          {busy ? "Removing…" : "Remove passkey"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function YourPasskeys({
  actions,
  create,
}: {
  actions: PasskeyActions;
  /** `navigator.credentials.create` unless a caller supplies one. */
  create?: CredentialCreator;
}) {
  const [rows, setRows] = useState<PasskeyRow[] | null>(null);
  const [mfa, setMfa] = useState<MfaStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PasskeyRow | null>(null);
  const [removed, setRemoved] = useState<Removed | null>(null);

  // The console rebuilds its actions every render; the list is read once on
  // mount and after each change, not on every render.
  const live = useRef(actions);
  live.current = actions;
  const refresh = useCallback(async () => {
    try {
      const [listed, status] = await Promise.all([live.current.list(), live.current.readMfa()]);
      setRows(listed);
      setMfa(status);
      setError(null);
    } catch (failure) {
      setError(messageOf(failure));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="panel" id="passkeys" aria-label="Your passkeys">
      <div className="panel__head">
        <h2>Your passkeys</h2>
        {rows === null ? null : <Chip>{rows.length} enrolled</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">These are yours, in every workspace you use.</p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {error !== null && mfa === null ? (
          // Unread, neither step-up can be offered: read again rather than guess (#739).
          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={() => {
                setError(null);
                void refresh();
              }}
            >
              Try again
            </button>
          </div>
        ) : null}
        {added === null ? null : (
          <Notice tone="good">
            Passkey “{added}” added. Your other sessions were signed out.
          </Notice>
        )}
        {removed?.kind === "removed" ? (
          <Notice tone="good">Passkey “{removed.name}” removed.</Notice>
        ) : null}
        {removed?.kind === "gone" ? (
          <Notice tone="bad">
            Passkey “{removed.name}” was already removed, from another device or session. The
            list below has been read again.
          </Notice>
        ) : null}
        {rows === null && error === null ? <p className="muted">Reading your passkeys…</p> : null}
        {rows !== null && rows.length === 0 ? <p className="muted">No passkeys yet.</p> : null}
      </div>
      {rows !== null && rows.length > 0 ? (
        <PasskeyTable
          rows={rows}
          onRemove={(row) => {
            setAdding(false);
            setAdded(null);
            setRemoved(null);
            setRemoving(row);
          }}
        />
      ) : null}
      {mfa === null || removing === null ? null : (
        <Remove
          key={removing.credential_id}
          actions={actions}
          mfa={mfa}
          row={removing}
          last={(rows ?? []).length === 1}
          onCancel={() => setRemoving(null)}
          onDone={async (outcome) => {
            setRemoving(null);
            setRemoved(outcome);
            await refresh();
          }}
        />
      )}
      {mfa === null || removing !== null ? null : adding ? (
        <Enrol
          actions={actions}
          mfa={mfa}
          create={create}
          onCancel={() => setAdding(false)}
          onDone={async (name) => {
            setAdding(false);
            setAdded(name || "Unnamed passkey");
            await refresh();
          }}
        />
      ) : (
        <div className="panel__body actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setAdded(null);
              setRemoved(null);
              setAdding(true);
            }}
          >
            Add a passkey
          </button>
        </div>
      )}
    </section>
  );
}
